<script setup lang="ts">
import {nextTick, onUnmounted, ref} from "vue";
import {GridApi} from "ag-grid-enterprise";
import {FindChangedEvent} from "ag-grid-community";
import Translator from "@/utils/common/Translator.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";

const props = defineProps<{
  gridApi: GridApi | undefined;
}>();

const showFindBar = ref(false);
const findInput = ref('');
const findActiveMatch = ref(0);
const findTotalMatches = ref(0);
const findInputRef = ref<HTMLInputElement>();

function onFindChanged(event: FindChangedEvent) {
  const {activeMatch, totalMatches, findSearchValue} = event;
  if (findSearchValue?.length) {
    findActiveMatch.value = activeMatch?.numOverall ?? 0;
    findTotalMatches.value = totalMatches;
  } else {
    findActiveMatch.value = 0;
    findTotalMatches.value = 0;
  }
}

async function toggleFindBar() {
  showFindBar.value = !showFindBar.value;
  if (showFindBar.value) {
    props.gridApi?.addEventListener('findChanged', onFindChanged);
    await nextTick(() => findInputRef.value?.focus());
  } else {
    closeFindBar();
  }
}

function closeFindBar() {
  showFindBar.value = false;
  findInput.value = '';
  findActiveMatch.value = 0;
  findTotalMatches.value = 0;
  props.gridApi?.removeEventListener('findChanged', onFindChanged);
  props.gridApi?.setGridOption('findSearchValue', undefined);
}

function onFindInput(value: string | null | number) {
  findInput.value = value ? value + '' : '';
  props.gridApi?.setGridOption('findSearchValue', findInput.value || undefined);
}

function findNext() {
  props.gridApi?.findNext();
}

function findPrevious() {
  props.gridApi?.findPrevious();
}

function onFindKeyDown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault();
    if (event.shiftKey) {
      findPrevious();
    } else {
      findNext();
    }
  } else if (event.key === 'Escape') {
    closeFindBar();
  } else if ((event.key === 'F' || event.key === 'f') && event.altKey) {
    event.preventDefault();
    toggleFindBar();
  }
}

onUnmounted(() => {
  if (showFindBar.value) {
    props.gridApi?.removeEventListener('findChanged', onFindChanged);
  }
});

defineExpose({toggleFindBar, showFindBar});
</script>

<template>
  <div v-if="showFindBar" @click.stop @mousedown.stop @keydown.stop @keyup.stop class="tw-flex tw-flex-row tw-gap-2 tw-items-center">
    <span v-if="findInput" class="tw-text-xs !tw-text-gray-500 dark:!tw-text-gray-400 tw-w-fit"
          v-text="findActiveMatch +'/'+ findTotalMatches"/>
    <q-input
      outlined dense hide-bottom-space
      ref="findInputRef"
      type="text"
      :model-value="findInput"
      @update:model-value="onFindInput"
      @keydown="onFindKeyDown"
      :placeholder="Translator.translateId('search')"
      class="nama-customize-dense-input nama-focused-input nama-field-input"
      input-class="nama-input-class"
    >
      <template v-slot:append>
        <a @click.stop="findPrevious"
           :title="Translator.translateId('previous') + ' (Shift+Enter)'"
           class="active-primary hover:tw-bg-sky-600/10 tw-border-gray-300 dark:tw-border-gray-600 tw-border hover:tw-bg-gray-200 tw-flex tw-items-center tw-justify-center active:tw-bg-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-md tw-cursor-pointer tw-shrink-0">
          <NamaIcon default-icon="keyboard_arrow_up" class="!tw-h-[15px] tw-w-[15px]"/>
        </a>
        <a @click.stop="findNext"
           :title="Translator.translateId('next') + ' (Enter)'"
           class="active-primary hover:tw-bg-sky-600/10 tw-border-gray-300 dark:tw-border-gray-600 tw-border hover:tw-bg-gray-200 tw-flex tw-items-center tw-justify-center active:tw-bg-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-md tw-cursor-pointer tw-shrink-0">
          <NamaIcon default-icon="keyboard_arrow_down" class="!tw-h-[15px] tw-w-[15px]"/>
        </a>
        <a @click.stop="closeFindBar"
           :title="Translator.translateId('close')"
           class="active-red hover:tw-bg-red-600/5 tw-border-gray-300 dark:tw-border-gray-600 tw-border hover:tw-bg-gray-200 tw-flex tw-items-center tw-justify-center active:tw-bg-gray-300 dark:hover:tw-bg-gray-700 dark:active:tw-bg-gray-600 tw-p-1 tw-rounded-md tw-cursor-pointer tw-shrink-0">
          <NamaIcon default-icon="close" class="!tw-h-[15px] tw-w-[15px] "/>
        </a>
      </template>
    </q-input>
  </div>
</template>
